/**
 * Layout contract for the alarm tree — where an alarm's notification toggles
 * sit relative to the alarm's name, as a desktop table (flexible name column
 * plus one fixed column per channel, headed once at the top) versus a phone
 * (no header; each toggle takes a full-width line naming its own channel).
 *
 * Contains: the layout constants (CHANNEL_COLUMN_WIDTH, TREE_PX,
 * *_NAME_INDENT, MOBILE_CHANNEL_INDENT) and the sx resolvers
 * (buildGridColumns, resolveColumnHeaderSx, resolveTreeRowSx,
 * resolveNameCellSx, resolveChannelCellSx). Returns plain JS — no JSX.
 */

/** Width of one channel column on the desktop table. */
export const CHANNEL_COLUMN_WIDTH = 80;

// Header and rows share this horizontal padding so each channel's header label
// sits over the control it names, and so text never runs against the border of
// the flush container the tree renders into.
export const TREE_PX = 2;

/** Indent of a child alarm under its category, in theme spacing units. */
export const DESKTOP_NAME_INDENT = 5;

// A phone cannot spare the desktop indent — 40px of a 360px viewport is width
// the alarm name needs more than the tree needs depth cues.
export const MOBILE_NAME_INDENT = 2;

// On a phone the channel lines hang under the name they belong to; the extra
// step distinguishes them from the next alarm's name.
export const MOBILE_CHANNEL_INDENT = 1.5;

/**
 * Builds the desktop grid template: a flexible name column plus one fixed
 * column per channel.
 *
 * @param {number} channelCount - How many channel columns the caller renders.
 * @returns {string} A `grid-template-columns` value.
 */
export function buildGridColumns(channelCount) {
  return `minmax(0, 1fr) repeat(${channelCount}, ${CHANNEL_COLUMN_WIDTH}px)`;
}

/**
 * Geometry of the channel header row.
 *
 * Desktop-only: on a phone the columns it heads no longer exist, and each
 * control names itself instead.
 *
 * @param {number} channelCount - How many channel columns the caller renders.
 * @returns {object} An `sx` fragment — the caller supplies its own surface colors.
 */
export function resolveColumnHeaderSx(channelCount) {
  return {
    display: { xs: 'none', md: 'grid' },
    gridTemplateColumns: buildGridColumns(channelCount),
    alignItems: 'center',
    columnGap: 2,
    px: TREE_PX,
    pt: 1.25,
    pb: 1,
  };
}

/**
 * Geometry of one tree row — a category header or an alarm.
 *
 * Desktop keeps the table columns. Mobile collapses to a single column, where
 * the name takes the first line and each channel follows on its own; the row
 * gap tightens so those lines read as one alarm rather than as separate rows.
 *
 * @param {number} channelCount - How many channel columns the caller renders.
 * @returns {object} An `sx` fragment — the caller supplies its own borders and colors.
 */
export function resolveTreeRowSx(channelCount) {
  return {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: buildGridColumns(channelCount) },
    alignItems: 'center',
    columnGap: 2,
    rowGap: { xs: 0.5, md: 2 },
    px: TREE_PX,
    py: 1.5,
  };
}

/**
 * Indent of a row's name cell.
 *
 * @param {object} [params]
 * @param {boolean} [params.indented=false] - Row is a child of a category header.
 * @returns {object} An `sx` fragment.
 */
export function resolveNameCellSx({ indented = false } = {}) {
  return {
    minWidth: 0,
    pl: indented ? { xs: MOBILE_NAME_INDENT, md: DESKTOP_NAME_INDENT } : 0,
  };
}

/**
 * Placement of a single channel's control within a row.
 *
 * Desktop centres the control under its header column and adds no padding, so
 * the column stays aligned with the label above it. Mobile spreads the control
 * against its own visible label, matching the label-left / control-right idiom
 * the rest of the settings rows use.
 *
 * @param {object} [params]
 * @param {boolean} [params.indented=false] - Row is a child of a category header.
 * @returns {object} An `sx` fragment.
 */
export function resolveChannelCellSx({ indented = false } = {}) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    minWidth: 0,
    justifyContent: { xs: 'space-between', md: 'center' },
    pl: { xs: (indented ? MOBILE_NAME_INDENT : 0) + MOBILE_CHANNEL_INDENT, md: 0 },
  };
}
